/**
 * Somebody else's profile.
 *
 * The public counterpart to the dashboard, and deliberately not the same
 * component: what may be shown is the entire difference between them, and a
 * shared component with a `private` flag is how a balance eventually ends up
 * rendered on a stranger's screen.
 *
 * The API is the real boundary — this page can only draw what `/users/:name`
 * chose to send, which is why there is no balance, no address and no staked
 * total anywhere below.
 */
import { Link, useParams } from 'react-router-dom'

import { Avatar } from '../components/Avatar'
import { EmptyState, SectionHeading, Skeleton, Stat } from '../components/ui'
import { PullTile } from './Dashboard'
import { useProfile } from '../hooks/queries'
import { useAuth } from '../hooks/useAuth'
import { dateOnly, multiple, percent } from '../lib/format'

export function ProfilePage() {
  const { username } = useParams<{ username: string }>()
  const { data, isLoading, error } = useProfile(username)
  const { user } = useAuth()

  const isMe = Boolean(user?.username && user.username === username?.toLowerCase())

  if (isLoading) {
    return (
      <div className="space-y-4 py-6">
        <Skeleton className="h-40" />
        <Skeleton className="h-64" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="py-10">
        <EmptyState
          title="No such profile"
          body="That handle does not exist, or its owner has made their profile private."
          action={
            <Link to="/leaderboard" className="btn btn-primary btn-sm">
              Back to the leaderboard
            </Link>
          }
        />
      </div>
    )
  }

  const { identity, stats, bestPulls } = data

  return (
    <div className="py-6">
      <div className="panel relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              'radial-gradient(70% 120% at 15% 0%, var(--color-brand-wash) 0%, transparent 60%)',
          }}
          aria-hidden
        />
        <div className="relative flex flex-wrap items-start gap-5 p-6 sm:p-8">
          <Avatar identity={identity} size={88} />

          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{identity.handle}</h1>
            {data.bio && (
              <p
                className="mt-2 max-w-lg text-sm leading-relaxed"
                style={{ color: 'var(--color-muted)' }}
              >
                {data.bio}
              </p>
            )}
            <p className="mt-2 text-[0.6875rem]" style={{ color: 'var(--color-faint)' }}>
              Joined {dateOnly(data.joinedAt)}
              {stats.firstOpenAt ? ` · ripping since ${dateOnly(stats.firstOpenAt)}` : ''}
            </p>
          </div>

          {isMe && (
            <div className="flex shrink-0 gap-2">
              <Link to="/settings" className="btn btn-ghost btn-sm">
                Edit profile
              </Link>
              <Link to="/me" className="btn btn-quiet btn-sm">
                Dashboard
              </Link>
            </div>
          )}
        </div>

        <div className="relative grid gap-6 border-t px-6 py-5 sm:grid-cols-4 sm:px-8">
          <Stat label="Packs opened" value={stats.opens.toLocaleString()} />
          <Stat label="Cards pulled" value={stats.cards.toLocaleString()} />
          <Stat
            label="Best rip"
            value={stats.bestMultiple === null ? '-' : multiple(stats.bestMultiple)}
            tone={stats.bestMultiple !== null && stats.bestMultiple >= 1 ? 'up' : undefined}
          />
          <Stat
            label="Realised RTP"
            value={stats.realisedRtp === null ? '-' : percent(stats.realisedRtp, 1)}
            sub={stats.opens < 20 ? 'too few opens to read' : 'against a published 54–63%'}
          />
        </div>
      </div>

      <section className="mt-10">
        <SectionHeading title="Best pulls" sub={`The highest-value cards ${identity.handle} has pulled.`} />
        {bestPulls.length === 0 ? (
          <EmptyState title="Nothing pulled yet" body="This account has not opened a pack." />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6">
            {bestPulls.map((pull) => (
              <PullTile key={pull.id} pull={pull} />
            ))}
          </div>
        )}
      </section>

      {/* Staked and returned are not published for other people. Realised RTP
          is a ratio and says something about luck; the absolute size of
          somebody else's spending says nothing useful and invites exactly the
          comparison this product should not be encouraging. */}
      <p className="mt-8 max-w-2xl text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
        Profiles show results, not spending. How much someone has staked is never published.
      </p>
    </div>
  )
}
